import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Card, CardContent, Button, Stack, CircularProgress, Alert, Chip, IconButton } from '@mui/material';
import { useDocument } from '../hooks/useFirestore';
import { db } from '../firebase';
import { updateDoc, doc } from 'firebase/firestore';
import { useEffect } from 'react';
import { MarkEmailUnread as MarkUnreadIcon } from '@mui/icons-material';

interface Inquiry {
  id: string;
  name?: string;
  email?: string;
  companyName?: string | null;
  message?: string;
  createdAt?: any;
  isRead?: boolean;
  readAt?: any;
}

export default function InquiryDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: inquiry, loading, error } = useDocument<Inquiry>('inquiries', id || '');

  useEffect(() => {
    if (inquiry && !inquiry.isRead && id) {
      const markAsRead = async () => {
        try {
          await updateDoc(doc(db, 'inquiries', id), {
            isRead: true,
            readAt: new Date(),
          });
        } catch (error) {
          console.error('Failed to mark inquiry as read:', error);
        }
      };
      markAsRead();
    }
  }, [inquiry, id]);

  const handleMarkAsUnread = async () => {
    if (!id) return;
    try {
      await updateDoc(doc(db, 'inquiries', id), {
        isRead: false,
        readAt: null,
      });
    } catch (error) {
      console.error('Failed to mark inquiry as unread:', error);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          お問い合わせの読み込みに失敗しました: {error.message}
        </Alert>
        <Button variant="outlined" onClick={() => navigate(-1)}>
          戻る
        </Button>
      </Box>
    );
  }

  if (!inquiry) {
    return (
      <Box>
        <Alert severity="warning" sx={{ mb: 2 }}>
          お問い合わせが見つかりません
        </Alert>
        <Button variant="outlined" onClick={() => navigate(-1)}>
          戻る
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h4">お問い合わせ詳細</Typography>
          {!inquiry.isRead ? <Chip label="未読" color="error" size="small" /> : <Chip label="既読" color="success" size="small" />}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {inquiry.isRead && (
            <IconButton onClick={handleMarkAsUnread} title="未読に戻す" color="primary">
              <MarkUnreadIcon />
            </IconButton>
          )}
          <Button variant="outlined" onClick={() => navigate(-1)}>
            戻る
          </Button>
        </Box>
      </Stack>

      <Card>
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            {(() => {
              const v: any = (inquiry as any)?.createdAt;
              try {
                const d = typeof v?.toDate === 'function' ? v.toDate() : new Date(v);
                return d.toLocaleString();
              } catch {
                return '';
              }
            })()}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <strong>名前:</strong> {inquiry.name || '（未入力）'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <strong>メール:</strong> {inquiry.email || '（未入力）'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            <strong>会社名:</strong> {inquiry.companyName || '（なし）'}
          </Typography>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            お問い合わせ内容
          </Typography>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            {inquiry.message || '（未入力）'}
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}


